import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allComics, commentsByComicId } from '@/mocks/comics';

function getArticleJsonLd(comic: typeof allComics[0]) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://ekaichem.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': comic.category, 'item': `http://ekaichem.com/category/${comic.categorySlug}` },
          { '@type': 'ListItem', 'position': 3, 'name': comic.title },
        ],
      },
      {
        '@type': 'Article',
        'headline': comic.title,
        'alternativeHeadline': comic.subtitle,
        'description': comic.description.substring(0, 160),
        'author': { '@type': 'Person', 'name': comic.author },
        'publisher': { '@id': 'http://ekaichem.com/#organization' },
        'url': `http://ekaichem.com/article/${comic.id}`,
        'image': comic.image,
        'inLanguage': 'zh-CN',
        'datePublished': '2026-01-01',
        'dateModified': '2026-07-03',
        'about': [{ '@type': 'Thing', 'name': comic.category }, { '@type': 'Thing', 'name': '韩漫' }],
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': comic.rating,
          'bestRating': 10,
          'ratingCount': comic.views,
        },
      },
    ],
  };
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comicId = id || 'comic-fantasy-01';
  const comic = allComics.find((c) => c.id === comicId) || allComics[0];
  const comments = commentsByComicId[comic.id] || [];
  const relatedComics = comic.relatedIds
    .map((rid) => allComics.find((c) => c.id === rid))
    .filter(Boolean) as typeof allComics;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [comicId]);

  const [visibleChapters, setVisibleChapters] = useState(8);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: comic.category, href: `/category/${comic.categorySlug}` },
    { label: comic.title },
  ];

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(getArticleJsonLd(comic)) }}
        />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-20">
          <Breadcrumb items={breadcrumbItems} />

          <article>
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12 py-10 md:py-14">
              <div className="lg:w-[320px] flex-shrink-0">
                <div className="sticky top-24">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-background-100">
                    <img
                      src={comic.image}
                      alt={comic.title}
                      title={`${comic.title} - ${comic.category}韩漫封面`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="bg-secondary-100 text-secondary-800 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                    {comic.category}
                  </span>
                  <span className={`text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap ${comic.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-600 border border-background-200/70'}`}>
                    {comic.status}
                  </span>
                  {comic.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="bg-background-100 text-foreground-500 text-xs px-2.5 py-1 rounded-full border border-background-200/60 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-400 italic mb-6">{comic.subtitle}</p>

                <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-background-200/70">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold font-heading text-accent-600">{comic.rating}</span>
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span key={i} className={`w-3.5 h-3.5 flex items-center justify-center ${i < Math.floor(comic.rating) ? 'text-accent-500' : 'text-foreground-200'}`}>
                          <i className={`text-xs ${i < Math.floor(comic.rating) ? 'ri-star-fill' : 'ri-star-line'}`}></i>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </span>
                    {(comic.views / 10000).toFixed(1)}万 阅读
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line"></i>
                    </span>
                    作者：{comic.author}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-file-list-3-line"></i>
                    </span>
                    共 {comic.totalChapters} 话
                  </div>
                </div>

                <div className="mb-10">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                    {comic.description}
                  </p>
                </div>

                <div className="mb-10">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="font-heading text-lg font-bold text-foreground-950">章节目录</h2>
                    <span className="text-xs text-foreground-400">共 {comic.totalChapters} 话</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {comic.chapters.slice(0, visibleChapters).map((ch, idx) => (
                      <button
                        key={ch.num}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-300 hover:bg-background-50 transition-all duration-150 text-left group cursor-pointer"
                      >
                        <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors duration-150">
                          第{ch.num}话 {ch.title}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.updateTime}</span>
                      </button>
                    ))}
                  </div>
                  {visibleChapters < comic.chapters.length && (
                    <div className="text-center mt-5">
                      <button
                        onClick={() => setVisibleChapters(Math.min(visibleChapters + 12, comic.chapters.length))}
                        className="px-6 py-2.5 rounded-full border border-background-300 text-sm text-foreground-600 font-medium whitespace-nowrap hover:border-primary-400 hover:text-primary-600 transition-all duration-200"
                      >
                        查看更多章节
                        <span className="w-4 h-4 inline-flex items-center justify-center ml-1">
                          <i className="ri-arrow-down-s-line"></i>
                        </span>
                      </button>
                    </div>
                  )}
                </div>

                {comments.length > 0 && (
                  <section className="mb-10">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-heading text-lg font-bold text-foreground-950">
                        读者评论
                        <span className="text-sm font-normal text-foreground-400 ml-2">({comments.length})</span>
                      </h2>
                    </div>
                    <div className="space-y-4">
                      {comments.map((cmt) => (
                        <div key={cmt.id} className="bg-background-100 rounded-xl border border-background-200/60 p-5">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                              <img src={cmt.avatar} alt={cmt.user} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-semibold text-foreground-900">{cmt.user}</span>
                                <div className="flex gap-0.5">
                                  {Array.from({ length: cmt.rating }).map((_, i) => (
                                    <span key={i} className="w-3 h-3 flex items-center justify-center text-accent-500">
                                      <i className="ri-star-fill text-[10px]"></i>
                                    </span>
                                  ))}
                                </div>
                                <span className="text-xs text-foreground-400 ml-auto whitespace-nowrap">{cmt.time}</span>
                              </div>
                              <p className="text-sm text-foreground-600 leading-relaxed mt-1.5">{cmt.content}</p>
                              <div className="flex items-center gap-4 mt-3">
                                <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-600 transition-colors duration-150 cursor-pointer">
                                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                                    <i className="ri-thumb-up-line"></i>
                                  </span>
                                  {cmt.likes}
                                </button>
                                <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-600 transition-colors duration-150 cursor-pointer">
                                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                                    <i className="ri-chat-1-line"></i>
                                  </span>
                                  回复
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>

            {relatedComics.length > 0 && (
              <section className="pb-20 md:pb-28">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">同类推荐</h2>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="flex items-center gap-1 text-sm text-primary-600 hover:underline whitespace-nowrap"
                  >
                    查看更多
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/article/${rc.id}`}
                      className="group block"
                    >
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-100 mb-3">
                        <img
                          src={rc.image}
                          alt={rc.title}
                          title={`${rc.title} - ${rc.category}韩漫推荐`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                          loading="lazy"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                          <span className="text-white text-xs font-medium">{rc.chapters[0].title}</span>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors duration-150">
                        {rc.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-accent-600 font-medium">{rc.rating}</span>
                        <span className="text-xs text-foreground-400">{rc.category}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}